import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { Card, CardContent, Button } from '@/components/ui';
import { ArrowLeft, Sun, Moon, User, EyeOff, ChevronRight } from 'lucide-react';

export function SettingsPage() {
  const { isAuthenticated, dbUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hidden } = useUserPreferences();

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4">
      <div className="mx-auto max-w-lg">
        {/* Back link */}
        <Link
          to="/"
          className="mb-6 flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-text)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <h1 className="mb-6 text-2xl font-bold">Settings</h1>

        <div className="space-y-4">
          {/* Account */}
          {isAuthenticated && dbUser && (
            <Card>
              <CardContent className="p-4">
                <Link
                  to="/settings/account"
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-primary/10">
                      <User className="h-5 w-5 text-accent-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{dbUser.name || 'User'}</p>
                      <p className="text-sm text-[var(--color-muted)]">{dbUser.email}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[var(--color-muted)]" />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Appearance */}
          <Card>
            <CardContent className="p-4">
              <h2 className="mb-4 font-semibold">Appearance</h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? (
                    <Moon className="h-5 w-5 text-[var(--color-muted)]" />
                  ) : (
                    <Sun className="h-5 w-5 text-[var(--color-muted)]" />
                  )}
                  <span>Theme</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className="capitalize"
                >
                  {theme} Mode
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Hidden Bars */}
          <Card>
            <CardContent className="p-4">
              <Link
                to="/settings/hidden"
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <EyeOff className="h-5 w-5 text-[var(--color-muted)]" />
                  <div>
                    <span>Hidden Bars</span>
                    {hidden.size > 0 && (
                      <p className="text-sm text-[var(--color-muted)]">
                        {hidden.size} bar{hidden.size === 1 ? '' : 's'} hidden
                      </p>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[var(--color-muted)]" />
              </Link>
            </CardContent>
          </Card>

          {/* Sign in prompt */}
          {!isAuthenticated && (
            <Card>
              <CardContent className="p-4 text-center">
                <p className="mb-4 text-[var(--color-muted)]">
                  Sign in to save favorites and access more features
                </p>
                <Link to="/login">
                  <Button>Sign In</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
